import { useReducer, useCallback, useEffect, useState } from 'react';
import { 
  BoardType, 
  GameStateType, 
  GameActionType, 
  PlayerType, 
  PositionType,
  GameSettingsType
} from '../types';
import { getAIMove } from '../lib/aiService';

// Constants
const BOARD_SIZE = 15;
const WIN_COUNT = 5;

// Default game settings
const defaultSettings: GameSettingsType = {
  gameMode: 'human-vs-human',
  aiDifficulty: 'medium',
  humanPlayerColor: 'BLACK',
};

// Initialize an empty board
const initializeBoard = (): BoardType => {
  return Array(BOARD_SIZE).fill(null).map(() => Array(BOARD_SIZE).fill(null));
};

// Initial game state
const initialState: GameStateType = {
  board: initializeBoard(),
  currentPlayer: 'BLACK', // Black goes first in Gomoku
  winner: null,
  gameOver: false,
  lastMove: null,
  moveCount: 0,
  settings: defaultSettings,
  isAITurn: false,
};

// Function to check for a win
const checkWin = (board: BoardType, position: PositionType, player: PlayerType): boolean => {
  const { row, col } = position;
  
  // Check all 4 directions: horizontal, vertical, diagonal up-right, diagonal up-left
  const directions = [
    { rowDir: 0, colDir: 1 },  // horizontal
    { rowDir: 1, colDir: 0 },  // vertical
    { rowDir: 1, colDir: 1 },  // diagonal down-right
    { rowDir: 1, colDir: -1 }, // diagonal down-left
  ];
  
  for (const direction of directions) {
    const { rowDir, colDir } = direction;
    let count = 1; // Start with 1 (the stone just placed)
    
    // Check forward direction
    for (let i = 1; i < WIN_COUNT; i++) {
      const r = row + rowDir * i;
      const c = col + colDir * i;
      
      // Check if position is valid and has the player's stone
      if (
        r >= 0 && r < BOARD_SIZE && 
        c >= 0 && c < BOARD_SIZE && 
        board[r][c] === player
      ) {
        count++;
      } else {
        break;
      }
    }
    
    // Check backward direction
    for (let i = 1; i < WIN_COUNT; i++) {
      const r = row - rowDir * i;
      const c = col - colDir * i;
      
      // Check if position is valid and has the player's stone
      if (
        r >= 0 && r < BOARD_SIZE && 
        c >= 0 && c < BOARD_SIZE && 
        board[r][c] === player
      ) {
        count++;
      } else {
        break;
      }
    }
    
    // If 5 or more in a row, we have a winner
    if (count >= WIN_COUNT) {
      return true;
    }
  }
  
  return false;
};

// Game reducer
const gameReducer = (state: GameStateType, action: GameActionType): GameStateType => {
  switch (action.type) {
    case 'PLACE_STONE': {
      const { position } = action;
      const { row, col } = position;
      
      // If the cell is already occupied or game is over, do nothing
      if (state.board[row][col] !== null || state.gameOver) {
        return state;
      }
      
      // Create a new board with the placed stone
      const newBoard = state.board.map((r, i) => 
        r.map((c, j) => (i === row && j === col) ? state.currentPlayer : c)
      );
      
      // Check if the current move results in a win
      const hasWon = checkWin(newBoard, position, state.currentPlayer);
      
      // Check if the board is full (draw)
      const moveCount = state.moveCount + 1;
      const isBoardFull = moveCount === BOARD_SIZE * BOARD_SIZE;
      
      // Determine if it's AI's turn next
      const nextPlayer = state.currentPlayer === 'BLACK' ? 'WHITE' : 'BLACK';
      const isAITurn = state.settings.gameMode === 'human-vs-ai' && 
                     nextPlayer !== state.settings.humanPlayerColor && 
                     !hasWon && 
                     !isBoardFull;
      
      return {
        ...state,
        board: newBoard,
        currentPlayer: nextPlayer,
        winner: hasWon ? state.currentPlayer : null,
        gameOver: hasWon || isBoardFull,
        lastMove: position,
        moveCount,
        isAITurn,
      };
    }
    
    case 'RESET_GAME': {
      // Create a completely new board
      const freshBoard = initializeBoard();
      
      // Check if AI should start first in human vs AI mode
      const isAIFirst = state.settings.gameMode === 'human-vs-ai' && 
                       state.settings.humanPlayerColor === 'WHITE';
      
      return {
        ...initialState,
        board: freshBoard,
        settings: state.settings,
        isAITurn: isAIFirst,
      };
    }

    case 'UPDATE_SETTINGS': {
      // Create a completely new board
      const freshBoard = initializeBoard();
      
      const newSettings = {
        ...state.settings,
        ...action.settings
      };
      
      // Determine if AI should start first with new settings
      const isAIFirst = newSettings.gameMode === 'human-vs-ai' && 
                       newSettings.humanPlayerColor === 'WHITE';
      
      return {
        ...initialState,
        board: freshBoard,
        settings: newSettings,
        isAITurn: isAIFirst,
      };
    }
    
    default:
      return state;
  }
};

// Custom hook for the Gomoku game
export const useGomokuGame = () => {
  const [state, dispatch] = useReducer(gameReducer, initialState);
  const [aiIsThinking, setAiIsThinking] = useState(false);
  
  // Place a stone on the board
  const placeStone = useCallback((position: PositionType) => {
    // Only allow human moves when it's not AI's turn
    if (!state.isAITurn) {
      dispatch({ type: 'PLACE_STONE', position });
    }
  }, [state.isAITurn]);
  
  // Reset the game
  const resetGame = useCallback(() => {
    dispatch({ type: 'RESET_GAME' });
  }, []);
  
  // Update game settings
  const updateSettings = useCallback((settings: Partial<GameSettingsType>) => {
    console.log('Updating settings:', settings); // Debug log
    dispatch({ type: 'UPDATE_SETTINGS', settings });
  }, []);

  // AI move effect
  useEffect(() => {
    console.log('AI turn state changed:', state.isAITurn); // Debug log
    console.log('Current settings:', state.settings); // Debug log
    
    if (state.isAITurn && !state.gameOver) {
      setAiIsThinking(true);
      
      // Add a slight delay to make AI move feel more natural
      const timer = setTimeout(() => {
        // Get the AI color (opposite of human player)
        const aiColor = state.settings.humanPlayerColor === 'BLACK' ? 'WHITE' : 'BLACK';
        console.log('AI making move as:', aiColor); // Debug log
        
        // Get AI move based on current difficulty
        const aiMove = getAIMove(state.board, state.settings.aiDifficulty, aiColor);
        console.log('AI selected move:', aiMove); // Debug log
        
        // Make the move
        if (aiMove && aiMove.row >= 0) {
          dispatch({ type: 'PLACE_STONE', position: aiMove });
        }
        
        setAiIsThinking(false);
      }, 700); // 700ms delay for thinking time
      
      return () => clearTimeout(timer);
    }
  }, [state.isAITurn, state.board, state.gameOver, state.settings]);
  
  return {
    board: state.board,
    currentPlayer: state.currentPlayer,
    winner: state.winner,
    gameOver: state.gameOver,
    lastMove: state.lastMove,
    moveCount: state.moveCount,
    settings: state.settings,
    isAITurn: state.isAITurn,
    aiIsThinking,
    placeStone,
    resetGame,
    updateSettings,
  };
};